import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ImageCard from "./ImageCard";
import config from "../config";

const formatBytes = (bytes) => {
  if (!bytes) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const value = bytes / 1024 ** index;
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDate = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return value;
  }
};

const buildDownloadUrl = (path) => {
  if (!path) {
    return null;
  }
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  try {
    const base = new URL(config.apiBaseUrl);
    return `${base.origin}${normalizedPath}`;
  } catch {
    const fallbackBase = config.apiBaseUrl?.replace(/\/$/, "") || "";
    return `${fallbackBase}${normalizedPath}`;
  }
};

const ImageDetailDrawer = ({ open, onClose, image, onDelete }) => {
  const theme = useTheme();

  const handleClose = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  const metaFields = React.useMemo(() => {
    if (!image) {
      return [];
    }
    return [
      { label: "Filename", value: image.filename },
      { label: "MIME Type", value: image.mime_type },
      {
        label: "Dimensions",
        value:
          image.width && image.height
            ? `${image.width} × ${image.height}`
            : "—",
      },
      { label: "Aspect Ratio", value: image.aspect_ratio },
      { label: "File Size", value: formatBytes(image.size) },
      { label: "Uploaded", value: formatDate(image.created_at) },
      { label: "Printful Error", value: image.printful_error },
      { label: "Printify Error", value: image.printify_error },
    ];
  }, [image]);

  const systemFields = React.useMemo(() => {
    if (!image) {
      return [];
    }
    return [
      { label: "Image ID", value: image.image_id },
      { label: "Record ID", value: image.id },
      { label: "Storage Path", value: image.path },
      { label: "Printify URL", value: image.image_url },
      { label: "Printful ID", value: image.printful_id ?? "—" },
      { label: "Printify ID", value: image.printify_id || "—" },
    ];
  }, [image]);

  const printfulChip = React.useMemo(() => {
    if (!image) {
      return null;
    }
    if (image.printful_error) {
      return (
        <Tooltip title={image.printful_error} placement="top" arrow>
          <Chip label="Printful: Error" color="error" />
        </Tooltip>
      );
    }
    return (
      <Chip
        label={image.sync_printful ? "Printful: Synced" : "Printful: Pending"}
        color={image.sync_printful ? "success" : "warning"}
        variant={image.sync_printful ? "filled" : "outlined"}
      />
    );
  }, [image]);

  const printifyChip = React.useMemo(() => {
    if (!image) {
      return null;
    }
    if (image.printify_error) {
      return (
        <Tooltip title={image.printify_error} placement="top" arrow>
          <Chip label="Printify: Error" color="error" />
        </Tooltip>
      );
    }
    if (!image.printify_id && !image.printify_shops?.length) {
      return null;
    }
    const label = image.printify_id ? "Printify: Synced" : "Printify: Pending";
    const color = image.printify_id ? "success" : "warning";
    const variant = image.printify_id ? "filled" : "outlined";
    return <Chip label={label} color={color} variant={variant} />;
  }, [image]);

  const downloadLinks = React.useMemo(() => {
    if (!image) {
      return [];
    }
    const entries = [
      { label: "Printify", url: image.image_url },
      {
        label: "Original",
        url: image.original_url || `/v1/images/${image.id}/content`,
      },
      { label: "Thumbnail", url: image.thumbnail_url },
      { label: "Upscaled", url: image.upscaled_url },
      { label: "Printful", url: image.printful_url },
    ]
      .map(({ label, url }) => ({ label, url: buildDownloadUrl(url) }))
      .filter(({ url }) => !!url);
    return entries;
  }, [image]);

  const printifyShopsSection = React.useMemo(() => {
    if (!image || !Array.isArray(image.printify_shops)) {
      return null;
    }
    const shops = image.printify_shops.filter(Boolean);
    if (!shops.length) {
      return null;
    }
    return (
      <>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          Printify Stores
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {shops.map((shop) => {
            const key = shop.id || shop.title || shop.sales_channel;
            const subtitle = shop.sales_channel
              ? ` (${shop.sales_channel})`
              : "";
            const label = `${shop.title || "Unnamed Store"}${subtitle}`;
            return <Chip key={key} label={label} variant="outlined" />;
          })}
        </Stack>
      </>
    );
  }, [image]);

  const handleDeleteClick = React.useCallback(() => {
    if (!image || !onDelete) {
      return;
    }
    if (window.confirm("Delete this image? This cannot be undone.")) {
      onDelete(image);
    }
  }, [image, onDelete]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "85vw", lg: "75vw", xl: "65vw" },
          maxWidth: 1280,
        },
      }}
    >
      {image ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: "100%",
          }}
        >
          <Box
            sx={{
              flex: { xs: "0 0 auto", md: "0 0 55%" },
              borderRight: { md: `1px solid ${theme.palette.divider}` },
              p: { xs: 2, md: 3 },
              position: { md: "sticky" },
              top: 0,
              height: { xs: "auto", md: "100vh" },
              maxHeight: { md: "100vh" },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Preview
            </Typography>
            <Box sx={{ flex: 1, minHeight: { xs: 320, md: 0 } }}>
              <ImageCard
                image={image}
                disableOverlay
                prefetchOriginal
                height="100%"
                hidePrintfulStatus={true}
                containerSx={{
                  height: "100%",
                  borderRadius: 3,
                  boxShadow: "none",
                  backgroundColor: "background.paper",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: { xs: "auto", md: "100vh" },
            }}
          >
            <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, overflowY: "auto" }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ wordBreak: "break-word" }}
              >
                {image.filename || "Image details"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {printfulChip}
                {printifyChip}
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                {metaFields.map(({ label, value }) =>
                  value == null || value === "" ? null : (
                    <Grid item xs={12} sm={6} key={label}>
                      <Typography variant="overline" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {value}
                      </Typography>
                    </Grid>
                  ),
                )}
              </Grid>

              {printifyShopsSection}

              {downloadLinks.length ? (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Downloads
                  </Typography>
                  <Stack spacing={1}>
                    {downloadLinks.map(({ label, url }) => (
                      <Link
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body1"
                        underline="hover"
                      >
                        {label}
                      </Link>
                    ))}
                  </Stack>

                  {systemFields.length ? (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" gutterBottom>
                        System
                      </Typography>
                      <Grid container spacing={2}>
                        {systemFields.map(({ label, value }) =>
                          value == null || value === "" ? null : (
                            <Grid item xs={12} sm={6} key={label}>
                              <Typography
                                variant="overline"
                                color="text.secondary"
                              >
                                {label}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ wordBreak: "break-all" }}
                              >
                                {value}
                              </Typography>
                            </Grid>
                          ),
                        )}
                      </Grid>
                    </>
                  ) : null}

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Delete
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Removing the image will permanently delete the file and any
                    derived assets.
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteClick}
                  >
                    Delete Image
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ padding: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Select an image to see its details.
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default ImageDetailDrawer;
